/**
 * Hand-curated catalog of paths the user can filter on in the scope-filter
 * builder (SMT-1608 Phase 1). Each entry maps a path string to the type
 * family that determines which operators are valid for that path.
 *
 * Path syntax (for user-authored `grouping.filter` predicates): Orion's
 * `ContextTagger` registers paths as dot-separated names (`Account.Name`,
 * `Asset.Ticker`, `Activity.Value`), built up from ~80
 * *ContextTagConfiguration classes in
 * OAT/Common/OAS.DataModel/Reporting/ContextConfiguration/. The OAT docs
 * (Orion-Reporting-Scope-API-Advanced-Grouping-Features.md) show some
 * examples in `group-info:<Entity>:<Property>` colon syntax for these
 * user-authored predicates, but the runtime resolver for `grouping.filter`
 * only knows the dot form (verified against
 * `ContextTagger.AddPlaceholdersToDictionary`, which builds paths with
 * `${path}.${node}`). Predicates in the colon form silently fail to
 * resolve and match nothing, which presents to the user as "the filter
 * excluded everything."
 *
 * Note: the `buildFilteredChildRequest` path-builder in
 * `lib/buildOrionRequest.js` legitimately emits `group-info:<Entity>:Name`
 * predicates -- that's a **separate** Orion shortcut for child-request
 * entity scoping (selecting one bucket out of a `grouping` calc) and is
 * resolved by a different code path on the server. The two syntaxes
 * coexist; only `grouping.filter` (the user-authored predicates this
 * catalog targets) requires the dot form.
 *
 * `Account.Name` resolves to the EntityName db column (i.e. the actual
 * account name string); `Account.DisplayName` resolves to the display
 * override. The OAT preset filters (HasValue, HasUnits) use
 * `Activity.Value` and `Activity.EndUnits` paths -- same dot form.
 *
 * `group` is an optional grouping hint used to organize the path picker.
 */
export const FILTER_PATHS = [
  // --- Group identity / metadata ---
  // Account.Name resolves server-side to the EntityName db column (the
  // canonical account name string). Account.DisplayName resolves to the
  // configured display override. We surface both since some firms
  // configure display names separately from canonical names.
  { path: 'Account.Name', label: 'Account Name', type: 'string', group: 'Account' },
  { path: 'Account.DisplayName', label: 'Account Display Name', type: 'string', group: 'Account' },
  { path: 'Account.Number', label: 'Account Number', type: 'string', group: 'Account' },
  { path: 'Account.Nickname', label: 'Account Nickname', type: 'string', group: 'Account' },
  { path: 'Account.AccountActiveStatus', label: 'Account Active Status', type: 'string', group: 'Account' },
  { path: 'Account.OutsideID', label: 'Outside ID', type: 'string', group: 'Account' },
  { path: 'Asset.Name', label: 'Asset Name', type: 'string', group: 'Asset' },
  { path: 'Asset.Ticker', label: 'Ticker', type: 'string', group: 'Asset' },
  { path: 'Asset.Status', label: 'Asset Status', type: 'string', group: 'Asset' },
  { path: 'AssetCategory.Name', label: 'Asset Category', type: 'string', group: 'Asset' },
  { path: 'AssetClass.Name', label: 'Asset Class', type: 'string', group: 'Asset' },
  { path: 'Product.Name', label: 'Product Name', type: 'string', group: 'Product' },
  { path: 'Product.Ticker', label: 'Product Ticker', type: 'string', group: 'Product' },
  { path: 'Household.FullName', label: 'Household Full Name', type: 'string', group: 'Household' },
  { path: 'Household.Country', label: 'Household Country', type: 'string', group: 'Household' },
  { path: 'Registration.Name', label: 'Registration Name', type: 'string', group: 'Registration' },

  // --- Calculated values (filtered against the surrounding contextChange unless overridden per-predicate) ---
  { path: 'Activity.Value', label: 'Market Value', type: 'currency', group: 'Calculated' },
  { path: 'Activity.EndUnits', label: 'Units', type: 'decimal', group: 'Calculated' },
  { path: 'Performance.Value', label: 'Performance', type: 'percent', group: 'Calculated' },
  { path: 'Performance.Contribution', label: 'Contribution', type: 'percent', group: 'Calculated' },
  { path: 'Performance.AverageWeight', label: 'Avg Weight', type: 'percent', group: 'Calculated' }
]

/** Map for O(1) catalog lookup by path. */
export const FILTER_PATHS_BY_PATH = Object.fromEntries(
  FILTER_PATHS.map(p => [p.path.toLowerCase(), p])
)

/**
 * Resolves a (possibly free-text / case-insensitive) path string to its
 * catalog entry. Returns undefined if the path isn't in the catalog -- the
 * caller is responsible for offering a fallback type-picker UX.
 */
export function lookupFilterPath (path) {
  if (!path || typeof path !== 'string') return undefined
  return FILTER_PATHS_BY_PATH[path.toLowerCase()]
}

/**
 * `CalculationGrouping` values surfaced for the per-predicate `context`
 * dropdown. `Self` (the default) means "evaluate at the surrounding
 * grouping". Selecting a deeper grouping causes Orion to walk down to that
 * level, evaluate the predicate per child, and rebuild the asset bucket
 * -- enabling the "filter at the Account level, affect Household rollups"
 * pattern called out in the ticket.
 */
export const CONTEXT_GROUPINGS = [
  { value: 'Self', label: 'Self (default)' },
  { value: 'Household', label: 'Household' },
  { value: 'Account', label: 'Account' },
  { value: 'Registration', label: 'Registration' },
  { value: 'Product', label: 'Product' },
  { value: 'Asset', label: 'Asset' },
  { value: 'AssetClass', label: 'Asset Class' },
  { value: 'SecurityClassification1', label: 'Security Classification 1' },
  { value: 'SecurityClassification2', label: 'Security Classification 2' },
  { value: 'SecurityClassification3', label: 'Security Classification 3' },
  { value: 'SecurityClassification4', label: 'Security Classification 4' }
]
